import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import CardExampleSvg from '../../assets/prosper-card.svg';
import { colors, TABLET_BREAKPOINT } from '../constants';

/* A credit card image with a dynamic customer name */

interface GeneratorProps {
  firstName: string;
  lastName: string;
  offerPreview?: boolean;
}

interface StyledProps extends React.HTMLProps<HTMLDivElement> {
  offerPreview?: boolean;
}

interface CardTextProps extends StyledProps {
  cardWidth: number;
}

const CardExampleContainer = styled.div<StyledProps>`
  position: relative;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    margin: auto 0 0;
    max-width: 13rem;
  }
  ${(p: StyledProps) =>
    p.offerPreview &&
    `
    margin: auto 0 0;
    max-width: 12.5625rem;
  `}
`;
CardExampleContainer.displayName = 'CardExampleContainer';

const CardExample = styled(CardExampleSvg)<StyledProps>`
  border-radius: 0.875rem;
  height: auto;
  width: 100%;
  box-shadow: 0 0.28125rem 0.28125rem rgba(0, 0, 0, 0.12);
  @media (max-width: ${TABLET_BREAKPOINT}) {
    box-shadow: 0 0.21875rem 0.21875rem rgba(0, 0, 0, 0.12);
  }
  ${(p: StyledProps) =>
    p.offerPreview &&
    `
    box-shadow: 0.375rem 0.3125rem 0.75rem rgba(0, 0, 0, 0.1);
  `}
`;
CardExample.displayName = 'CardExample';

const LiveNameDisplay = styled.p<CardTextProps>`
  max-width: 12.5rem;
  hyphens: auto;
  position: absolute;
  left: 7%;
  bottom: 14.75%;
  // font-size from card width: 1/25th of it, /16 for px to rem
  font-size: ${(p: CardTextProps) => `${p.cardWidth / 25 / 16}rem`};
  font-weight: 600;
  letter-spacing: 0.0625rem;
  color: ${colors.TEXT_SECONDARY};
  text-transform: uppercase;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    max-width: 8.625rem;
    font-size: 0.5625rem;
    letter-spacing: 0.03125rem;
  }
  font-size: ${(p: CardTextProps) => p.offerPreview && '0.5625rem'};
`;
LiveNameDisplay.displayName = 'LiveNameDisplay';

export const CreditCardNameGenerator: FC<GeneratorProps> = ({
  firstName,
  lastName,
  offerPreview = false,
}) => {
  const [name, setName] = useState('');
  const [cardWidth, setCardWidth] = useState(220); // The width of the card, used to set font-size
  const cardContainerRef = useRef(null);

  // Truncate the full name to 23 characters or fewer on the preview card in CreditCardInfo
  // Truncation strategies:
  // 1 - No truncation
  // 2 - First initial, blank last name (first name exceeds 23 characters)
  // 3 - First initial, full last name
  // 4 - First initial, truncated last name with ellipses
  // 5 - Truncated last name with ellipses, blank first name
  const formatName = () => {
    const first = firstName.trim();
    const last = lastName.trim();
    const firstNameLastName = `${first} ${last}`.trim();
    const firstInitialLastName = `${first.charAt(0)} ${last}`.trim();
    // Return placeholder when both first and last names are empty
    if (firstNameLastName.length === 0) return 'CARDHOLDER NAME';
    if (firstNameLastName.length > 23) {
      // Truncate last name if first name is blank and last name exceeds 23 characters
      if (first.length === 0 && last.length > 23)
        return `${last.substr(0, 23)}...`;
      // Return first initial and full last name when length is no more than 23 characters
      if (firstInitialLastName.length <= 23) return firstInitialLastName;
      // Return first initial and truncated last name when length exceeds 23 characters
      return `${firstInitialLastName.substr(0, 23)}...`;
    }
    // Return non-truncated full name when it does not exceed 23 characters in length
    return firstNameLastName;
  };

  useEffect(() => {
    const formattedName = formatName();
    setName(formattedName);
  }, [firstName, lastName]);

  useLayoutEffect(() => {
    // When the DOM updates, measure the width of the card
    setCardWidth(cardContainerRef.current.offsetWidth);
  }, []);

  return (
    <CardExampleContainer offerPreview={offerPreview} ref={cardContainerRef}>
      <CardExample id="card-example-image" alt="card-image-illustrative-purposes-only" />
      <LiveNameDisplay
        id="card-example-name"
        offerPreview={offerPreview}
        cardWidth={cardWidth}
      >
        {name}
      </LiveNameDisplay>
    </CardExampleContainer>
  );
};

